import { useEffect, useRef, useState } from 'react';
import logo from '../../assets/company-logo.png';
import loginBackground from '../../assets/jukun-cloud-login-background.png';
import { errorMessage } from '../../common/api/httpClient';
import type { AuthSession } from '../../model/portalModels';
import { authApi } from './authApi';
import { acquireFeishuCode } from './feishuBridge';
import { canLoadFeishuSdk } from './feishuEnvironment';
import { withAbortTimeout } from './feishuRecovery';
import { consumeFeishuWebCallback, feishuWebLoginReturnPath,
  hasFeishuWebCallback, hasFeishuWebStartHandoff, startFeishuWebLogin } from './feishuWebLogin';
import { LoginView, type LoginContent } from './LoginView';
import { readFeishuEntry } from '../../navigation/portalNavigation';

const loginContent: LoginContent = {
  platformName: '巨鲲云平台',
  welcomeText: '欢迎登录',
  usernamePlaceholder: '请输入用户名',
  passwordPlaceholder: '请输入密码',
  businessHighlights: ['项目智管', '监管监测', 'AI设计', '智慧机械'],
};

export function LoginPage(props: {
  onLogin: (session: AuthSession) => void;
  autoLoginFailed?: boolean;
  onRetryFeishu?: () => void;
  externalError?: string;
}) {
  const inFlight = useRef(false);
  const [identifier, setIdentifier] = useState('admin');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [feishuAppId, setFeishuAppId] = useState<string | undefined>(
    () => readFeishuEntry(window.location.search).appId,
  );
  const [feishuLoading, setFeishuLoading] = useState(() => hasFeishuWebCallback(window.location.search)
    || hasFeishuWebStartHandoff(window.location.search));
  const callbackStarted = useRef(false);
  const handoffStarted = useRef(false);
  const clientLoginController = useRef<AbortController | undefined>(undefined);

  useEffect(() => () => clientLoginController.current?.abort(), []);

  useEffect(() => {
    if (handoffStarted.current || !hasFeishuWebStartHandoff(window.location.search)) return;
    handoffStarted.current = true;
    const entry = readFeishuEntry(window.location.search);
    window.history.replaceState({}, '', entry.appId
      ? `/login?feishuAppId=${encodeURIComponent(entry.appId)}` : '/login');
    if (entry.invalid || !entry.appId) {
      setError('飞书登录入口无效，请重新发起');
      setFeishuLoading(false);
      return;
    }
    try {
      startFeishuWebLogin(entry.appId);
    } catch (startError) {
      setError(errorMessage(startError));
      setFeishuLoading(false);
    }
  }, []);

  useEffect(() => {
    if (callbackStarted.current || !hasFeishuWebCallback(window.location.search)) return;
    callbackStarted.current = true;
    const callbackSearch = window.location.search;
    window.history.replaceState({}, '', '/login');
    let callback: ReturnType<typeof consumeFeishuWebCallback>;
    try {
      callback = consumeFeishuWebCallback(callbackSearch);
      setFeishuAppId(callback.appId);
      window.history.replaceState({}, '', `/login?feishuAppId=${encodeURIComponent(callback.appId)}`);
    } catch (callbackError) {
      setError(errorMessage(callbackError));
      setFeishuLoading(false);
      return;
    }
    if (callback.error || !callback.code) {
      setError(callback.error ?? '飞书授权未完成');
      setFeishuLoading(false);
      return;
    }
    inFlight.current = true;
    void authApi.feishuSession(callback.appId, callback.code, undefined,
      `${window.location.origin}/login`)
      .then(() => window.location.replace(feishuWebLoginReturnPath(callback.appId)))
      .catch(requestError => {
        setError(errorMessage(requestError));
        setFeishuLoading(false);
        inFlight.current = false;
      });
  }, []);

  useEffect(() => {
    if (feishuAppId || hasFeishuWebCallback(window.location.search)) return;
    let active = true;
    void authApi.defaultFeishuApp()
      .then(result => { if (active && result.appId) setFeishuAppId(result.appId); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [feishuAppId]);

  const login = async () => {
    if (inFlight.current || feishuLoading) return;
    if (!identifier.trim() || !password) {
      setError('请输入账号和密码');
      return;
    }
    inFlight.current = true;
    setLoading(true);
    setError(undefined);
    try {
      props.onLogin(await authApi.login(identifier.trim(), password));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const loginWithFeishu = () => {
    if (!feishuAppId || inFlight.current || feishuLoading) return;
    setError(undefined);
    if (canLoadFeishuSdk(feishuAppId)) {
      const controller = new AbortController();
      clientLoginController.current = controller;
      inFlight.current = true;
      setFeishuLoading(true);
      void (async () => {
        try {
          const code = await withAbortTimeout(
            acquireFeishuCode(feishuAppId, controller.signal), controller,
          );
          await withAbortTimeout(
            authApi.feishuSession(feishuAppId, code, controller.signal), controller,
          );
          if (!controller.signal.aborted) {
            window.location.replace(feishuWebLoginReturnPath(feishuAppId));
          }
        } catch (loginError) {
          if (!controller.signal.aborted || loginError instanceof Error
              && loginError.message === '自动登录超时') {
            setError(errorMessage(loginError));
          }
        } finally {
          if (clientLoginController.current === controller) {
            clientLoginController.current = undefined;
            inFlight.current = false;
            setFeishuLoading(false);
          }
        }
      })();
      return;
    }
    try {
      startFeishuWebLogin(feishuAppId);
    } catch (startError) {
      setError(errorMessage(startError));
    }
  };

  return <LoginView
    content={loginContent}
    backgroundSrc={loginBackground}
    logoSrc={logo}
    identifier={identifier}
    password={password}
    loading={loading}
    feishuLoading={feishuLoading}
    feishuAvailable={Boolean(feishuAppId)}
    error={error ?? props.externalError}
    autoLoginFailed={props.autoLoginFailed}
    onRetryFeishu={props.onRetryFeishu}
    onIdentifierChange={setIdentifier}
    onPasswordChange={setPassword}
    onLogin={() => void login()}
    onFeishuLogin={loginWithFeishu}
  />;
}
